import * as React from 'react';
import { Link, Redirect } from 'react-router-dom';
import { signIn, getOTP, verifyAndLogin } from '../../utils/api/authApis';
import { setLocalActiveRole, updateTokenForPersistnce } from '../../utils/authUtils';
import TextInputWithIcon from '../../components/common/TextInputs/TextInputWithIcon';
import ButtonFilled from '../../components/common/Buttons/ButtonFilled';
import IconButton from '../../components/common/Buttons/IconButton';
import { LOGIN_BANNER } from '../../utils/constants/images';
import { LOGO2 } from '../../utils/constants/images';
import { useAuthStore } from '../../stores/authStore';
import { UserInfo } from '../../types/auth';
import { MAIL_ICON } from '../../utils/constants/icons';
import { getUserProfile } from '../../utils/api/userApi';
import { Roles } from '../../utils/enums';

declare global {
    interface Window {
        grecaptcha: any;
    }
}

const OtpLogin = () => {
    const authenticated = useAuthStore((state) => state.authenticated);
    const setUserDetails = useAuthStore((state) => state.setUserDetails);
    const setAccessToken = useAuthStore((state) => state.setAccessToken);
    const setAuthenticated = useAuthStore((state) => state.setAuthenticated);
    const setActiveRole = useAuthStore((state) => state.setActiveRole);
    const setActivePartner = useAuthStore((state) => state.setActivePartner);

    const [email, setEmail] = React.useState('');
    const [screen, setScreen] = React.useState('email');
    const [otpResp, setOtpResp] = React.useState<any>(null);
    const [password, setPassword] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState('');

    const handleLogin = async (e) => {
        e.preventDefault();
        if (!email || !password) return;
        setLoading(true);
        window.grecaptcha?.enterprise.ready(function () {
            window.grecaptcha?.enterprise
                .execute(process.env.REACT_APP_SITE_KEY, { action: 'login' })
                .then(async function (token) {
                    try {
                        const response = await verifyAndLogin({ user_id: email, otp: password, request_id: otpResp?.request_id });
                        const data: UserInfo = response.data;
                        updateTokenForPersistnce(data.token);
                        setAccessToken(data.token);
                        const resp = await getUserProfile();
                        window.grecaptcha.enterprise.reset(process.env.REACT_APP_SITE_KEY);
                        document.getElementsByClassName('grecaptcha-badge')?.[0]?.setAttribute('style', 'visibility: hidden');
                        setUserDetails(resp.data);
                        let rolesName: any = [];
                        for (let i = 0; i < resp.data.roles.length; i++) {
                            const element = resp.data.roles[i];
                            rolesName.push(element.name);
                        }
                        if (rolesName.includes(Roles.ADMIN)) {
                            setActiveRole(Roles.ADMIN);
                            setLocalActiveRole(Roles.ADMIN);
                        } else if (rolesName.includes(Roles.REQUESTOR)) {
                            setActiveRole(Roles.REQUESTOR);
                            setLocalActiveRole(Roles.REQUESTOR);
                        } else if (rolesName.length) {
                            setActiveRole(rolesName[0]);
                            setLocalActiveRole(rolesName[0]);
                        }
                        const partner = resp.data.org || 'default';
                        setActivePartner(partner);
                        setAuthenticated(true);
                    } catch (e) {
                        console.error(e);
                        setError('Wrong Creds, Try again');
                        setLoading(false);
                    }
                })
                .catch((e) => {
                    console.error(e);
                    setError('ReCaptcha verification Failed, Reload and try again');
                    setLoading(false);
                });
        });
    };

    const handleGetOtp = async (e) => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        setError('');
        try {
            const resp = await getOTP({ email });
            setOtpResp(resp?.data);
            setScreen('otp');
            setPassword('');
        } catch (e) {
            console.error(e);
            setError('Wrong Creds, Try again');
            setOtpResp(null);
        }
        setLoading(false);
    };

    const clearStates = () => {
        setEmail('');
        setPassword('');
        setOtpResp(null);
        setError('');
        setScreen('email');
    };

    React.useEffect(() => {
        const loadScriptByURL = (id, url, callback) => {
            const isScriptExist = document.getElementById(id);

            if (!isScriptExist) {
                var script = document.createElement('script');
                script.type = 'text/javascript';
                script.src = url;
                script.id = id;
                script.onload = function () {
                    if (callback) callback();
                };
                document.body.appendChild(script);
            }

            if (isScriptExist && callback) {
                window.grecaptcha.enterprise.reset(process.env.REACT_APP_SITE_KEY);
                callback();
            }
        };

        // load the script by passing the URL - api.js -> enterprise.js
        loadScriptByURL('recaptcha-key', `https://www.google.com/recaptcha/enterprise.js?render=${process.env.REACT_APP_SITE_KEY}`, function () {
            console.log('Recaptcha loaded!');
        });
    }, []);

    if (!localStorage.getItem('token')) {
        updateTokenForPersistnce('');
        setAccessToken('');
        setUserDetails(null);
        setAuthenticated(false);
    } else if (authenticated) {
        return <Redirect to="/" />;
    }

    return (
        <div className="flex flex-wrap w-full">
            <div className="flex flex-col w-full md:w-1/2 sm:w-full">
                <div className="flex flex-col justify-center px-8 pt-8 my-auto md:justify-start md:pt-0">
                    <div className="flex justify-center mb-20">
                        <img id="loginLogo" src={LOGO2} alt="logo" style={{ width: 200 }} />
                    </div>
                    <p className="text-3xl text-center">
                        3D powered product
                        <br /> visuals for your category
                    </p>
                    {screen === 'email' ? (
                        <form className="flex flex-col pt-2 md:pt-8 w-full lg:w-1/2 md:w-1/2 sm:w-full m-auto" onSubmit={handleGetOtp}>
                            <TextInputWithIcon
                                value={email}
                                error
                                placeholder="Email"
                                onChange={(event) => {
                                    setEmail(event.target.value);
                                    if (error.length != 0) setError('');
                                }}
                            >
                                <svg width="15" height="15" fill="currentColor" viewBox="0 0 1792 1792" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1792 710v794q0 66-47 113t-113 47h-1472q-66 0-113-47t-47-113v-794q44 49 101 87 362 246 497 345 57 42 92.5 65.5t94.5 48 110 24.5h2q51 0 110-24.5t94.5-48 92.5-65.5q170-123 498-345 57-39 100-87zm0-294q0 79-49 151t-122 123q-376 261-468 325-10 7-42.5 30.5t-54 38-52 32.5-57.5 27-50 9h-2q-23 0-50-9t-57.5-27-52-32.5-54-38-42.5-30.5q-91-64-262-182.5t-205-142.5q-62-42-117-115.5t-55-136.5q0-78 41.5-130t118.5-52h1472q65 0 112.5 47t47.5 113z"></path>
                                </svg>
                            </TextInputWithIcon>
                            <div className="mt-5">
                                {/* <div 
                                id='recaptcha' className="g-recaptcha"
                                data-sitekey={process.env.REACT_APP_SITE_KEY}
                                data-callback="onSubmit"
                                data-size="invisible">
                            </div> */}
                                <div className="text-sm text-center text-red-700 mb-2" style={{ minHeight: 20 }}>
                                    {error}
                                </div>
                                <ButtonFilled disabled={loading} type="submit" fullWidth label={'Get OTP'} />
                            </div>
                        </form>
                    ) : (
                        <form className="flex flex-col pt-2 md:pt-8 w-full lg:w-1/2 md:w-1/2 sm:w-full m-auto" onSubmit={handleLogin}>
                            <TextInputWithIcon
                                value={password}
                                type="password"
                                placeholder="OTP"
                                onChange={(event) => {
                                    setPassword(event.target.value);
                                    if (error.length != 0) setError('');
                                }}
                            >
                                <svg width="15" height="15" fill="currentColor" viewBox="0 0 1792 1792" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1376 768q40 0 68 28t28 68v576q0 40-28 68t-68 28h-960q-40 0-68-28t-28-68v-576q0-40 28-68t68-28h32v-320q0-185 131.5-316.5t316.5-131.5 316.5 131.5 131.5 316.5q0 26-19 45t-45 19h-64q-26 0-45-19t-19-45q0-106-75-181t-181-75-181 75-75 181v320h736z"></path>
                                </svg>
                            </TextInputWithIcon>
                            <div className="mt-5">
                                <div className="text-sm text-center text-red-700 mb-2" style={{ minHeight: 20 }}>
                                    {error}
                                </div>
                                <div className="flex">
                                    <div className="mr-1">
                                        <ButtonFilled color="red" disabled={loading} onClick={clearStates} fullWidth label={'Cancel'} />
                                    </div>
                                    <ButtonFilled disabled={loading} type="submit" fullWidth label={'Login'} />
                                </div>
                            </div>
                        </form>
                    )}
                    {/* <div className="pt-6 pb-6 text-center w-full lg:w-1/2 md:w-1/2 sm:w-full m-auto">
                        <IconButton fullWidth label=" Sign In with Google" onClick={handleGoogleLogin} color="red">
                            <svg width="20" height="20" fill="currentColor" className="mr-2" viewBox="0 0 1792 1792" xmlns="http://www.w3.org/2000/svg">
                                <path d="M896 786h725q12 67 12 128 0 217-91 387.5t-259.5 266.5-386.5 96q-157 0-299-60.5t-245-163.5-163.5-245-60.5-299 60.5-299 163.5-245 245-163.5 299-60.5q300 0 515 201l-209 201q-123-119-306-119-129 0-238.5 65t-173.5 176.5-64 243.5 64 243.5 173.5 176.5 238.5 65q87 0 160-24t120-60 82-82 51.5-87 22.5-78h-436v-264z"></path>
                            </svg>
                        </IconButton>
                    </div> */}

                    <div className="pt-4 text-center">
                        <p>
                            I need access to the tool.&nbsp;
                            <a
                                href="https://docs.google.com/forms/d/e/1FAIpQLSchANM0jPz32mgH1Gin0aXVUmMvRJg8wadaCr0Gm6EcBtktuA/viewform"
                                target="_blank"
                                className="font-semibold underline text-blue-700"
                                rel="noreferrer"
                            >
                                Request Access
                            </a>
                        </p>
                    </div>
                </div>
            </div>
            <div className="w-1/2 shadow-2xl bg-cover">
                <img className="hidden object-cover w-full h-screen md:block" src={LOGIN_BANNER} />
            </div>
        </div>
    );
};

export default OtpLogin;
